// The PARTS of the composer's focus panel (DESIGN-COMPOSER-FOCUS-POPOVER-
// CONTEXT-INSPECT-1; the panel is _simone/Suggestions.js):
//
//   AiPanelLabel   a section's name — the quiet eyebrow ("Context",
//                  "Suggested for Overview", "Recent", "Mode")
//   AiPanelAction  one of the three first-class verbs (Inspect & ask,
//                  Summarize, Build a board): the kit's tonal capsule in its
//                  compact size, the verb's line glyph on the house plate
//   AiPanelGroup   a group of suggestion rows (role=group, named by its label)
//   AiPanelRow     one suggestion: THE house menu row (MenuPanelItem — its
//                  glyph plate slot, hover / focus / press, the inset focus
//                  ring) in the menu the panel's rows form; the generic
//                  staple reads one ink step quieter
//
// Every row and verb sends through the shell (`aiPanelSend`, `aiPanelRun` —
// workspace functions/aiPanel.js); nothing here builds a prompt.

export const AiPanelLabel = {
  tag: 'span',
  display: 'block',
  fontSize: 'Z',
  fontWeight: '500',
  lineHeight: '1.3',
  color: 'caption',
  padding: '0 X',
  overflowWrap: 'anywhere'
}

export const AiPanelAction = {
  extends: 'AppCapsuleBtnToolbar',
  compact: true,
  // The three share one track each (the panel's grid): a verb fills its
  // column, so the trio reads as one symmetric row in every language; a long
  // Georgian verb wraps inside its capsule (the kit's text law).
  width: '100%',
  minWidth: '0',
  gap: 'Y',
  // The verb's glyph on the plate; the capsule's hover and keyboard focus
  // invert it (the one state logic of a glyph plate).
  '&:hover [data-glyph-plate]': { background: 'highlight', color: 'title-reversed' },
  '&:focus-visible [data-glyph-plate]': { background: 'highlight', color: 'title-reversed' },
  ariaLabel: (el, s) => (s && s.label) || null,
  title: (el, s) => (s && s.hint) || null,
  // PHONES: three tiles — the glyph over the word, so each word keeps the
  // whole track (beside its glyph, "Summarize" broke as "Summariz / e" at 390
  // on dev 2026-10-04).
  '@mobileL': {
    flexDirection: 'column',
    gap: 'X',
    paddingBlock: 'Z',
    round: 'radiusCard',
    textAlign: 'center'
  },
  Plate: {
    dataGlyphPlate: true,
    ariaHidden: 'true',
    display: 'flex',
    align: 'center center',
    flexShrink: '0',
    boxSize: 'glyphPlateXs',
    round: 'radiusPill',
    color: 'caption',
    transition: 'background-color A defaultBezier, color A defaultBezier',
    Icon: { name: (el, s) => (s && s.glyph) || 'lnAssistant', boxSize: 'iconSm' }
  },
  ActionLabel: {
    tag: 'span',
    minWidth: '0',
    text: (el, s) => (s && s.label) || ''
  },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    el.call('aiPanelRun', s && (s.verb || s.key))
  }
}

export const AiPanelGroup = {
  role: 'group',
  ariaLabel: (el, s) => (s && s.label) || null,
  flow: 'y',
  gap: 'X',
  minWidth: '0',
  // The group's name for the eye; the group's own aria-label names it for
  // assistive technology, so this copy is hidden from it.
  AiPanelLabel: {
    ariaHidden: 'true',
    text: (el, s) => (s && s.label) || ''
  },
  GroupRows: {
    flow: 'y',
    gap: 'W',
    minWidth: '0',
    childExtends: 'AiPanelRow',
    childrenAs: 'state',
    children: (el, s) => {
      const rows = s && s.rows
      const list = rows && typeof rows.parse === 'function' ? rows.parse() : rows
      return Array.isArray(list) ? list : []
    }
  }
}

export const AiPanelRow = {
  extends: 'MenuPanelItem',
  // MenuPanelItem draws its Plate from the row's `glyph` (a line glyph name).
  glyph: (el, s) => (s && s.glyph) || null,
  text: (el, s) => (s && s.text) || '',
  title: (el, s) => (s && s.prompt && s.prompt !== s.text ? s.prompt : null),
  color: (el, s) => (s && s.generic ? 'paragraph' : 'title'),
  lineHeight: '1.35',
  // the house row's text law, restated where the panel's names live: a long
  // ask or a Georgian compound wraps inside the row, never widens the panel
  overflowWrap: 'anywhere',
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    const row = s && typeof s.parse === 'function' ? s.parse() : s
    el.call('aiPanelSend', row)
  }
}
